import { Injectable } from '@angular/core';
import {BreakpointObserver, BreakpointState} from "@angular/cdk/layout";
import {AndroidFullScreen} from "@awesome-cordova-plugins/android-full-screen";
import {Capacitor} from "@capacitor/core";
import {ThemeService} from "./theme.service";
import {StatusbarAndNavbarService} from "./statusbar-and-navbar.service";
import {SafeArea} from "capacitor-plugin-safe-area";
import { ScreenOrientation, OrientationType } from '@capawesome/capacitor-screen-orientation';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {

  mobileMaxWidth = 800;

  public singleView: boolean = false;
  fullscreen: boolean = false;
  fontSizeSheetActive: boolean = false;

  public platform: string = Capacitor.getPlatform();

  constructor(public breakpointObserver: BreakpointObserver, private statusBarAndNavbarService: StatusbarAndNavbarService, private themeService: ThemeService) {
    this.breakpointObserver
      .observe([`(min-width: ${this.mobileMaxWidth}px)`])
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          console.debug(`Viewport width is ${this.mobileMaxWidth}px or greater!`);
          this.singleView = false;
        } else {
          console.debug(`Viewport width is less than ${this.mobileMaxWidth}px!`);
          this.singleView = true;
        }
      });

    console.debug('Platform: ' + this.platform);

    // Refresh safe area whenever the DOM fullscreen state changes
    document.addEventListener('fullscreenchange', () => {
      this.refreshSafeAreaWithRetry({ initialDelayMs: 120, maxAttempts: 5, backoffFactor: 1.6 });
    });

    // Also watch visual viewport size changes (Android system bars show/hide often triggers this)
    if (window.visualViewport) {
      const debounced = this.debounce(() => {
        this.refreshSafeAreaWithRetry({ initialDelayMs: 50, maxAttempts: 4, backoffFactor: 1.5 });
      }, 100);
      window.visualViewport.addEventListener('resize', debounced);
    }
  }

  isNotBrowser() {
    return this.platform !== 'web';
  }

  fullscreenSupported() {
    return this.platform !== 'ios';
  }

  async exitFullScreen() {
    this.fullscreen = false;
    try {
      // Only attempt to exit if we are actually in fullscreen and the API is available.
      if (document.fullscreenElement && (document as any).exitFullscreen) {
        await (document as any).exitFullscreen();
      }
    } catch (err) {
      console.warn('Failed to exit fullscreen:', err);
    }

    AndroidFullScreen.isImmersiveModeSupported()
      .then(() => {
          AndroidFullScreen.showSystemUI();
          this.statusBarAndNavbarService.applyColor(this.themeService.getPrimaryColor(), this.themeService.isDarkMode());
        })
      .catch(console.warn)
      .then(() => {
        // Recompute insets after showing system UI with retries to avoid race conditions
        return this.refreshSafeAreaWithRetry({ initialDelayMs: 140, maxAttempts: 5, backoffFactor: 1.6 });
      });
  }

  async enterFullScreen() {
    try {
      const canRequest = (document.documentElement as any).requestFullscreen;
      if (canRequest && !document.fullscreenElement) {
        await (document.documentElement as any).requestFullscreen();
        this.fullscreen = true;
      } else {
        // If the API is not available or already in fullscreen, don't throw.
        this.fullscreen = !!document.fullscreenElement;
      }
    } catch (err) {
      this.fullscreen = !!document.fullscreenElement;
      console.warn('Failed to enter fullscreen:', err);
    }

    AndroidFullScreen.isImmersiveModeSupported()
      .then(() => AndroidFullScreen.immersiveMode())
      .catch(console.warn)
      .then(() => {
        // Recompute insets after the UI mode change
        return this.refreshSafeAreaWithRetry({ initialDelayMs: 80, maxAttempts: 4, backoffFactor: 1.5 });
      });
  }

  async toggleFullScreen() {
    if (this.fullscreen) {
      await this.exitFullScreen();
    } else {
      await this.enterFullScreen();
    }
  }

  /*
   * Safe Area
   */

  safeAreaInsets: any;

  async initSafeArea() {
    try {
      const { insets } = await SafeArea.getSafeAreaInsets();
      this.safeAreaInsets = insets;

      this.setCSSVars(insets);
      this.setRecommendedCSSVars(insets);

      const { statusBarHeight } = await SafeArea.getStatusBarHeight();
      console.debug(statusBarHeight, 'statusbarHeight');

      await SafeArea.removeAllListeners();
      await SafeArea.addListener('safeAreaChanged', (data) => {
        this.safeAreaInsets = data.insets;
        this.setCSSVars(data.insets);
        this.setRecommendedCSSVars(data.insets);
        console.debug('safeAreaChanged:', this.safeAreaInsets);
      });

      console.debug(this.safeAreaInsets);
    } catch (e) {
      console.info('SafeArea not available on this platform');
    }
  }

  private setCSSVars(insets: any) {
    for (const [key, value] of Object.entries(insets)) {
      document.documentElement.style.setProperty(`--safe-area-inset-${key}`, `${value}px`);
    }
  }

  private setRecommendedCSSVars(insets: any) {
    for (const [key, value] of Object.entries(insets)) {
      document.documentElement.style.setProperty(`--recommended-safe-area-inset-${key}`, `${value}px`);
    }
  }

  private async refreshSafeArea() {
    try {
      const { insets } = await SafeArea.getSafeAreaInsets();
      this.safeAreaInsets = insets;
      this.setCSSVars(insets);
      this.setRecommendedCSSVars(insets);
    } catch (e) {
      console.info('SafeArea not available on this platform');
    }
  }

  private sleep(ms: number) {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
  }

  private async refreshSafeAreaWithRetry(options?: { initialDelayMs?: number; maxAttempts?: number; backoffFactor?: number; }) {
    const initialDelayMs = options?.initialDelayMs ?? 80;
    const maxAttempts = options?.maxAttempts ?? 4;
    const backoffFactor = options?.backoffFactor ?? 1.5;

    let delay = initialDelayMs;
    const before = this.safeAreaInsets ? { ...this.safeAreaInsets } : undefined;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (delay > 0) {
        await this.sleep(delay);
      }
      try {
        const { insets } = await SafeArea.getSafeAreaInsets();
        const changed = !before || !this.insetsEqual(before, insets);
        this.safeAreaInsets = insets;
        this.setCSSVars(insets);
        this.setRecommendedCSSVars(insets);
        console.debug(`[DEBUG_LOG] refreshSafeAreaWithRetry attempt ${attempt}/${maxAttempts}:`, insets, `changed=${changed}`);
        if (changed || attempt === maxAttempts) {
          return;
        }
      } catch (e) {
        console.info('SafeArea not available on this platform');
        return;
      }
      delay = Math.round(delay * backoffFactor);
    }
  }

  private insetsEqual(a: any, b: any) {
    if (!a || !b) return false;
    return a.top === b.top && a.right === b.right && a.bottom === b.bottom && a.left === b.left;
  }

  private debounce<T extends (...args: any[]) => void>(fn: T, wait: number) {
    let t: any;
    return (...args: Parameters<T>) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  async noSafeArea() {
    try {
      await SafeArea.removeAllListeners();
      this.setCSSVars({
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      })
    } catch (e) {
      console.info('SafeArea not available on this platform');
    }
  }

  async lockOrientation(orientation: 'portrait' | 'landscape') {
    await ScreenOrientation.lock({ type: orientation === 'portrait' ? OrientationType.PORTRAIT : OrientationType.LANDSCAPE });
    await this.refreshSafeArea();
  }

  async unlockOrientation() {
    await ScreenOrientation.unlock();
    await this.refreshSafeArea();
  }
}
